/**
 * Labeled text input with field states, helper text, and optional icon.
 */

import * as React from 'react';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LucideIcon,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Info,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/** Visual validation state (drives border + trailing icon). */
type FieldState = 'default' | 'error' | 'success' | 'warning';
/** Height/padding token for the text input. */
type InputSize = 'sm' | 'md' | 'lg';

/** `aria-describedby` target for error or helper text. */
function describedById(id: string, error?: string, helperText?: string): string | undefined {
  if (error) return `${id}-error`;
  if (helperText) return `${id}-helper`;
  return undefined;
}

/** Trailing state icon; `null` for default. */
function iconForFieldState(fieldState: FieldState) {
  if (fieldState === 'error') return AlertCircle;
  if (fieldState === 'success') return CheckCircle2;
  if (fieldState === 'warning') return AlertTriangle;
  return null;
}

/** Adornment icon size class matching the input size. */
function inputIconSizeClass(size: InputSize) {
  if (size === 'sm') return 'modern-input-icon-sm';
  if (size === 'lg') return 'modern-input-icon-lg';
  return 'modern-input-icon-md';
}

/** Label row above the input, including optional tooltip. */
type InputLabelProps = Readonly<{
  label?: string;
  labelTooltip?: string;
  inputId: string;
  required?: boolean;
}>;

/** Optional label with required marker and info tooltip. */
function InputLabel({ label, labelTooltip, inputId, required }: InputLabelProps) {
  if (!label) return null;
  return (
    <Label htmlFor={inputId} className={cn('form-label', required && 'form-label-required')}>
      {label}
      {labelTooltip && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-help ml-1 opacity-50 align-middle">
                <Info size={13} />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{labelTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </Label>
  );
}

/** Composes size, variant, icon, and validation classes for the input. */
function buildInputClassName(args: {
  inputSize: InputSize;
  variant: string;
  Icon?: LucideIcon;
  iconPosition: 'left' | 'right';
  showStateIcon: boolean;
  shouldShowToggle: boolean;
  loading: boolean;
  effectiveState: FieldState;
  className?: string;
}) {
  return cn(
    `modern-input-${args.inputSize}`,
    `modern-input-${args.variant}`,
    args.Icon && args.iconPosition === 'left' && 'modern-input-with-icon-left',
    args.Icon && args.iconPosition === 'right' && 'modern-input-with-icon-right',
    args.showStateIcon && 'modern-input-with-icon-right',
    args.shouldShowToggle && 'modern-input-with-icon-right',
    args.loading && 'modern-input-with-icon-right',
    args.effectiveState === 'error' && 'modern-input-error',
    args.effectiveState === 'success' && 'modern-input-success',
    args.effectiveState === 'warning' && 'modern-input-warning',
    args.className,
  );
}

/** Leading/trailing icons, password toggle, and loading spinner. */
type InputAdornmentsProps = Readonly<{
  Icon?: LucideIcon;
  iconPosition: 'left' | 'right';
  iconSizeClass: string;
  showStateIcon: boolean;
  StateIcon: LucideIcon | null;
  effectiveState: FieldState;
  shouldShowToggle: boolean;
  showPassword: boolean;
  onTogglePassword: () => void;
  loading: boolean;
}>;

/** Renders the input's leading and trailing adornments. */
function InputAdornments({
  Icon,
  iconPosition,
  iconSizeClass,
  showStateIcon,
  StateIcon,
  effectiveState,
  shouldShowToggle,
  showPassword,
  onTogglePassword,
  loading,
}: InputAdornmentsProps) {
  const PasswordToggleIcon = showPassword ? EyeOff : Eye;
  return (
    <>
      {Icon && iconPosition === 'left' && (
        <Icon className={cn('modern-input-icon modern-input-icon-left', iconSizeClass)} />
      )}
      {showStateIcon && StateIcon && (
        <StateIcon
          className={cn(
            'modern-input-icon modern-input-icon-right',
            iconSizeClass,
            effectiveState === 'error' && 'text-destructive',
            effectiveState === 'success' && 'text-[rgb(10, 134, 66)]',
            effectiveState === 'warning' && 'text-[rgb(var(--color-warning))]',
          )}
        />
      )}
      {shouldShowToggle && (
        <button
          type="button"
          onClick={onTogglePassword}
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            'flex items-center justify-center',
            'w-10 h-10 rounded-lg',
            'text-[var(--color-1-nuance-2)]',
            'hover:text-[var(--color-1-nuance-1)]',
            'hover:bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]',
            'transition-all duration-200',
            'cursor-pointer',
            'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-2-nuance-1)]/40',
            'focus-visible:border-2 focus-visible:border-[var(--color-2-nuance-1)]',
          )}
          tabIndex={-1}
          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        >
          <PasswordToggleIcon className="h-5 w-5" />
        </button>
      )}
      {Icon && iconPosition === 'right' && !showStateIcon && !shouldShowToggle && !loading && (
        <Icon className={cn('modern-input-icon modern-input-icon-right', iconSizeClass)} />
      )}
      {loading && (
        <span className={cn('modern-input-icon modern-input-icon-right', iconSizeClass)}>
          <Loader2 className="h-full w-full animate-spin text-muted-foreground" />
        </span>
      )}
    </>
  );
}

/** Props for {@link Input}. */
interface CustomInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  labelTooltip?: string;
  error?: string;
  helperText?: string;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  validation?: (value: string) => string | undefined;
  variant?: 'default' | 'filled' | 'ghost';
  state?: FieldState;
  inputSize?: InputSize;
  showPasswordToggle?: boolean;
  loading?: boolean;
  /** Node aligned with the control, not the label (contact swap). */
  controlEnd?: React.ReactNode;
}

/** Labeled text field with validation state, adornments, and helper text. */
export const Input = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      label,
      labelTooltip,
      error,
      helperText,
      icon: Icon,
      iconPosition = 'left',
      className,
      id,
      required,
      variant = 'default',
      state = 'default',
      inputSize = 'md',
      showPasswordToggle = false,
      loading = false,
      controlEnd,
      type,
      ...props
    },
    ref,
  ) => {
    const inputId = id || `input-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;
    const [showPassword, setShowPassword] = React.useState(false);

    const effectiveState = error ? 'error' : state;
    const isPasswordInput = type === 'password';
    const shouldShowToggle = isPasswordInput && showPasswordToggle;
    const effectiveType = shouldShowToggle && showPassword ? 'text' : type;

    const StateIcon = iconForFieldState(effectiveState);
    const showStateIcon = effectiveState !== 'default' && !Icon && !shouldShowToggle && !loading;
    const iconSizeClass = inputIconSizeClass(inputSize);

    return (
      <div className="w-full space-y-2">
        <InputLabel
          label={label}
          labelTooltip={labelTooltip}
          inputId={inputId}
          required={required}
        />
        <div className={controlEnd ? 'flex items-center gap-2' : undefined}>
          <div className={controlEnd ? 'relative min-w-0 flex-1' : 'relative'}>
            <InputAdornments
              Icon={Icon}
              iconPosition={iconPosition}
              iconSizeClass={iconSizeClass}
              showStateIcon={showStateIcon}
              StateIcon={StateIcon}
              effectiveState={effectiveState}
              shouldShowToggle={shouldShowToggle}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              loading={loading}
            />
            <ShadcnInput
              ref={ref}
              id={inputId}
              type={effectiveType}
              className={buildInputClassName({
                inputSize,
                variant,
                Icon,
                iconPosition,
                showStateIcon,
                shouldShowToggle,
                loading,
                effectiveState,
                className,
              })}
              required={required}
              aria-invalid={effectiveState === 'error'}
              aria-describedby={describedById(inputId, error, helperText)}
              {...props}
            />
          </div>
          {controlEnd ? <div className="shrink-0">{controlEnd}</div> : null}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="form-error-text" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="form-helper-text">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
